import { useState, useEffect } from 'react'
import { useAuth } from '../../utils/platform_auth'
import PTRestAPI from '../../utils/pt_rest_api'
import { showNotification } from '../../components/Notification'
import articlePreview from './article-preview'
import { PLATFORM_API_URL, PLATFORM_API_VERSION } from '../../config'

const CDN = `${PLATFORM_API_URL}/${PLATFORM_API_VERSION}/blog-resource?file=`
let articleLoading = false

export default () => {
  const auth = useAuth()
  const [publishOpen, setPublishOpen] = useState(false)
  const [resourcesOpen, setResourcesOpen] = useState(false)
  const [browserOpen, setBrowserOpen] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [categoryOpen, setCategoryOpen] = useState(false)
  const [articlePayload, setArticlePayload] = useState({})
  const [htmlContent, setHTMLContent] = useState('')
  const [categoryID, setCategoryID] = useState('')
  const [refreshKey, setRefreshKey] = useState(false)
  const [editMode, setEditMode] = useState(
    globalThis['localStorage'] &&
      localStorage.getItem('articleEditMode') === 'true',
  )

  //when the local article is reloaded due to changes
  useEffect(() => {
    if (!articlePayload) return
    localStorage.setItem('articleEditMode', editMode ? 'true' : 'false')
    const preview = articlePreview()
    const htmlContent = editMode ? preview : articlePayload.content ?? preview
    loadHTMLContent(htmlContent)
  }, [editMode, articlePayload?.content])

  const updateQueryParams = (params) => {
    const url = new URL(window.location)
    Object.keys(params).forEach((key) => {
      if (params[key] !== null) {
        url.searchParams.set(key, params[key])
      } else {
        url.searchParams.delete(key)
      }
    })

    window.history.replaceState({}, '', url)
  }

  const getArticleContent = async (articleID) => {
    if (articleID === 'new') {
      setArticlePayload({
        articleID: '',
        title: '',
        description: '',
        tags: '',
        thumbnail: '',
        author: '',
        categoryID: [],
      })
      return articlePreview()
    }

    const article = await PTRestAPI.GetArticle(articleID)
    if (article.error) return '<h1>Article not found</h1>'
    //set the article payload
    setArticlePayload(article)
    return article.content
  }

  const copyHTMLSource = () => {
    const el = document.createElement('textarea')
    el.value = articlePayload.content
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
    showNotification({
      message: 'Article original HTML source copied to clipboard',
      className: 'bg-primary text-text_inv',
    })
  }

  const updateCDNUrls = (htmlString, cdnURL) => {
    // Create a new DOM parser
    const parser = new DOMParser()
    // Parse the HTML string into a DOM Document
    const doc = parser.parseFromString(htmlString, 'text/html')

    // Get all elements with the cdn attribute
    const elements = doc.querySelectorAll('[cdn]')

    // Loop through each element and update the src or href attribute
    elements.forEach((element) => {
      const cdnFile = element.getAttribute('cdn')
      if (cdnFile) {
        // Construct the new URL
        const newUrl = `${cdnURL}${cdnFile}`
        // Determine the attribute to update
        if (element.tagName.toLowerCase() === 'img') {
          element.setAttribute('src', newUrl)
        } else if (element.tagName.toLowerCase() === 'a') {
          element.setAttribute('href', newUrl)
        } else {
          // Handle other possible tags that can have URLs
          element.setAttribute('src', newUrl)
        }
        // Remove the cdn attribute
        element.removeAttribute('cdn')
      }
    })

    // Serialize the DOM Document back into a string
    return doc.documentElement.outerHTML
  }

  const loadHTMLContent = (htmlContent) => {
    //replace the [cdn] with the actual cdn url
    htmlContent = updateCDNUrls(htmlContent, CDN)

    import('dompurify').then((DOMPurify) => {
      //allow only youtube iframes
      DOMPurify.default.addHook('uponSanitizeElement', (node, data) => {
        if (data.tagName === 'iframe') {
          const src = node.getAttribute('src') || ''
          if (!src.startsWith('https://www.youtube.com/embed/')) {
            return node.parentNode.removeChild(node)
          }
        }
      })
      // Sanitize the HTML content
      const sanitized = DOMPurify.default.sanitize(htmlContent, {
        ADD_TAGS: ['iframe', 'a'], //or ALLOWED_TAGS
        ADD_ATTR: ['target'],
      })
      setHTMLContent(sanitized)
    })
  }

  const newArticle = () => {
    if (!globalThis['localStorage']) return

    const htmlContent =
      editMode || articlePayload.articleID == 'new'
        ? articlePreview()
        : articlePayload.content

    loadHTMLContent(htmlContent)
  }

  const loadArticle = async (articleID) => {
    if (articleLoading) return
    //reset the article
    setArticlePayload(null)
    setHTMLContent('')
    articleLoading = true
    const htmlContent = await getArticleContent(articleID)
    articleLoading = false
    loadHTMLContent(htmlContent)
    updateQueryParams({ article: articleID })

    setEditMode(articleID === 'new' ? true : editMode)
  }

  const refreshArticle = () => {
    if (
      !articlePayload ||
      !articlePayload.articleID ||
      articlePayload.articleID === 'new'
    )
      return null
    //load the content
    loadArticle(articlePayload.articleID)
  }

  const articleExists = async (articleID) => {
    return await PTRestAPI.ArticleExists(auth, articleID)
  }

  const newCategory = () => {
    setCategoryID('~new')
    setCategoryOpen(true)
    setRefreshKey(!refreshKey)
  }

  const loadCategory = async (categoryID) => {
    return await PTRestAPI.GetBlogCategory(categoryID)
  }
  const updateCategory = async (categoryPayload) => {
    return await PTRestAPI.UpdateBlogCategory(auth, categoryPayload)
  }

  const publishArticle = async (articlePayload) => {
    return await PTRestAPI.PublishArticle(auth, articlePayload)
  }

  return {
    publishArticle,
    setArticlePayload,
    getArticleContent,
    editMode,
    setEditMode,
    articlePayload,
    browserOpen,
    setBrowserOpen,
    publishOpen,
    setPublishOpen,
    resourcesOpen,
    setResourcesOpen,
    editorOpen,
    setEditorOpen,
    copyHTMLSource,
    newArticle,
    refreshArticle,
    loadArticle,
    htmlContent,
    updateQueryParams,
    setCategoryOpen,
    categoryOpen,
    categoryID,
    setCategoryID,
    newCategory,
    updateCategory,
    loadCategory,
    refreshKey,
    articleExists,
  }
}
